import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Copy, MoreVertical, Pencil, Plus, RotateCcw, Search, TextQuote } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { canWriteMainTab } from "@/lib/maintabAccess";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SP } from "@/constants/testIds";
import {
  duplicateSmartPhrase,
  editSmartPhrasePath,
  formatLabel,
  formatUpdated,
  listSmartPhrases,
  newSmartPhrasePath,
  statusLabel,
  updateSmartPhrase,
} from "@/lib/smartPhraseApi";
import "@/styles/emr-catalog.css";

function apiErrorMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg).join("; ");
  return fallback;
}

export default function SmartPhrasesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = canWriteMainTab(user?.maintabAccess, "smartphrases");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [format, setFormat] = useState("all");
  const [owner, setOwner] = useState("all");

  const filters = useMemo(
    () => ({
      q: search.trim() || undefined,
      active: status === "all" ? undefined : status === "active",
      format: format === "all" ? undefined : format,
      owner,
    }),
    [search, status, format, owner],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listSmartPhrases(filters);
      setItems(data.items || []);
    } catch (err) {
      setError(apiErrorMessage(err, "Unable to load SmartPhrases."));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const clearFilters = () => {
    setSearch("");
    setStatus("all");
    setFormat("all");
    setOwner("all");
  };

  const onDuplicate = async (phrase) => {
    try {
      const created = await duplicateSmartPhrase(phrase);
      toast.success(`Duplicated as ${created.name}.`);
      navigate(editSmartPhrasePath(created.id));
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not duplicate SmartPhrase."));
    }
  };

  const onToggleActive = async (phrase) => {
    try {
      await updateSmartPhrase(phrase.id, {
        name: phrase.name,
        description: phrase.description,
        content: phrase.content,
        format: phrase.format,
        active: !phrase.active,
      });
      toast.success(phrase.active ? "Marked inactive." : "Marked active.");
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not update status."));
    }
  };

  return (
    <div data-testid={SP.page} className="emr-catalog emr-catalog--full-table">
      <div className="emr-catalog__pagehead">
        <div>
          <h1 data-testid={SP.title} className="emr-catalog__title">
            SmartPhrase Manager
          </h1>
          <p className="emr-catalog__subtitle">
            Create, manage, and insert reusable documentation templates.
          </p>
        </div>
        {canWrite ? (
          <Button
            data-testid={SP.newBtn}
            className="btn emr-catalog__btn"
            onClick={() => navigate(newSmartPhrasePath())}
          >
            <Plus className="emr-catalog__btn-icon" /> New SmartPhrase
          </Button>
        ) : null}
      </div>

      <div className="emr-catalog__workspace">
        <section className="card emr-catalog__section">
          <h2 className="emr-catalog__section-title">Purpose</h2>
          <p className="emr-catalog__copy">
            Search and maintain reusable documentation templates for encounter notes.
          </p>
        </section>

        <section className="card emr-catalog__section">
          <h2 className="emr-catalog__section-title">Filters</h2>
            <div className="emr-catalog__filters emr-catalog__filters--search-row">
              <div className="emr-catalog__search">
                <Search className="emr-catalog__search-icon" aria-hidden="true" />
                <input
                  data-testid={SP.search}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search SmartPhrases by name or description..."
                  className="emr-catalog__input"
                />
              </div>
              <select
                aria-label="Status"
                data-testid={SP.statusFilter}
                value={status}
                onChange={(event) => setStatus(event.target.value)}
                className="emr-catalog__select"
              >
                <option value="all">Status: All</option>
                <option value="active">Status: Active</option>
                <option value="inactive">Status: Inactive</option>
              </select>
              <select
                aria-label="Format"
                data-testid={SP.formatFilter}
                value={format}
                onChange={(event) => setFormat(event.target.value)}
                className="emr-catalog__select"
              >
                <option value="all">Format: All</option>
                <option value="richtext">Format: Rich Text</option>
                <option value="plaintext">Format: Plain Text</option>
              </select>
              <select
                aria-label="Owner"
                data-testid={SP.ownerFilter}
                value={owner}
                onChange={(event) => setOwner(event.target.value)}
                className="emr-catalog__select"
              >
                <option value="me">Owner: Me</option>
                <option value="all">Owner: All</option>
              </select>
              <Button
                type="button"
                variant="outline"
                data-testid={SP.clearFilters}
                onClick={clearFilters}
                className="btn emr-catalog__btn emr-catalog__btn--ghost"
              >
                <RotateCcw className="emr-catalog__btn-icon" />
                Clear Filters
              </Button>
            </div>
        </section>

        <section className="card emr-catalog__section emr-catalog__catalog">
          <h2 className="emr-catalog__section-title">SmartPhrases</h2>
          {loading ? (
            <div data-testid={SP.loading} className="emr-catalog__status-msg">
              Loading…
            </div>
          ) : error ? (
            <div data-testid={SP.errorState} className="emr-catalog__error">
              <p className="emr-catalog__error-copy">{error}</p>
            </div>
          ) : items.length === 0 ? (
            <div data-testid={SP.emptyState} className="emr-catalog__empty">
              <TextQuote className="emr-catalog__empty-icon" aria-hidden="true" />
              <p>No SmartPhrases match these filters.</p>
              {canWrite ? (
                <Button variant="outline" className="btn emr-catalog__btn emr-catalog__btn--ghost" onClick={() => navigate(newSmartPhrasePath())}>
                  <Plus className="emr-catalog__btn-icon" /> New SmartPhrase
                </Button>
              ) : null}
            </div>
          ) : (
            <div className="emr-catalog__table-wrap">
              <table data-testid={SP.table} className="striped highlight emr-catalog__table emr-catalog__table--wide">
                <thead>
                  <tr>
                    <th>Name (Shortcut)</th>
                    <th>Description</th>
                    <th>Format</th>
                    <th>Owner</th>
                    <th>Status</th>
                    <th>Updated</th>
                    <th className="emr-catalog__actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.id} data-testid={`${SP.row}-${row.id}`}>
                      <td className="emr-catalog__name">
                        {canWrite ? (
                          <Link to={editSmartPhrasePath(row.id)} className="emr-catalog__link">
                            {row.name}
                          </Link>
                        ) : (
                          <span>{row.name}</span>
                        )}
                        {row.is_demo ? (
                          <span className="emr-catalog__demo">DEMO</span>
                        ) : null}
                      </td>
                      <td className="emr-catalog__muted">{row.description}</td>
                      <td>{row.formatLabel || formatLabel(row.format)}</td>
                      <td className="emr-catalog__muted">{row.ownerDisplay || "—"}</td>
                      <td>
                        <span className={`emr-catalog__status ${row.active ? "emr-catalog__status--active" : "emr-catalog__status--inactive"}`}>
                          {row.statusLabel || statusLabel(row.active)}
                        </span>
                      </td>
                      <td className="emr-catalog__muted">{formatUpdated(row.updatedAt)}</td>
                      <td>
                        <div className="emr-catalog__row-actions">
                          {canWrite ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Edit"
                              className="emr-catalog-icon-btn"
                              data-testid={`${SP.editBtn}-${row.id}`}
                              onClick={() => navigate(editSmartPhrasePath(row.id))}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          ) : null}
                          {canWrite ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Duplicate"
                              className="emr-catalog-icon-btn"
                              data-testid={`${SP.duplicateBtn}-${row.id}`}
                              onClick={() => onDuplicate(row)}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          ) : null}
                          {canWrite ? (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  title="More"
                                  className="emr-catalog-icon-btn"
                                  data-testid={`${SP.moreBtn}-${row.id}`}
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => navigate(editSmartPhrasePath(row.id))}>
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onDuplicate(row)}>
                                  Duplicate
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onToggleActive(row)}>
                                  {row.active ? "Mark Inactive" : "Mark Active"}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
